import React from 'react';
import { cn } from '../../lib/utils';
import { API_ENDPOINTS } from '../../config/apiConfig';
import { getDateFormat } from '../../config/appConfig';
import { CheckCircleFilledIcon, XCircleFilledIcon, FileIcon } from '../ui/Icons';
import {
  badgeClass,
  getBadgePresentationForColor,
  getBadgeStyleTokens,
  resolveBadgeEntity,
} from '../../config/tableBadgeStyles';
import { isRefinedTableUi } from '../../config/tableUiThemeRuntime';
import { normalizeFileName } from '../ui/utils/fileNameEncoding';

/**
 * Cell Renderer Functions
 * Each function receives: (value, column, row, theme, options)
 * Returns: React element or null
 */

export const renderColor = (value, column, row, theme) => {
  if (!value) return <span className={theme.text.muted}>No color</span>;
  const swatchClass = isRefinedTableUi()
    ? 'w-4 h-4 rounded-md border border-slate-200/80 dark:border-slate-600/50'
    : cn('w-4 h-4 rounded border', theme.border.default);
  return (
    <div className="flex items-center gap-2">
      <div
        className={swatchClass}
        style={{ backgroundColor: value }}
        title={value}
      />
      <span className={`text-xs ${theme.text.secondary}`}>{value}</span>
    </div>
  );
};

export const renderBoolean = (value, column, row, theme) => {
  if (isRefinedTableUi()) {
    return (
      <div className="flex justify-center">
        {value ? (
          <span className={badgeClass(getBadgeStyleTokens().emerald)}>Active</span>
        ) : (
          <span className={badgeClass(getBadgeStyleTokens().neutral)}>Inactive</span>
        )}
      </div>
    );
  }
  return (
    <div className="flex items-center justify-center">
      {value ? (
        <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
          <CheckCircleFilledIcon className="w-4 h-4" />
          <span className="text-sm font-medium">Active</span>
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400">
          <XCircleFilledIcon className="w-4 h-4" />
          <span className="text-sm font-medium">Inactive</span>
        </span>
      )}
    </div>
  );
};

export const renderBadge = (value, column, row, theme) => {
  if (!value) return <div className="flex justify-center"><span className={theme.text.muted}>—</span></div>;
  const entity = resolveBadgeEntity(column, row, value);
  const badgeValueRaw =
    entity?.name ||
    entity?.title ||
    entity?.label ||
    value?.name ||
    value;

  // Ensure React never receives a raw object as child (Firefox/React stricter rendering).
  const badgeValue =
    badgeValueRaw == null
      ? '—'
      : typeof badgeValueRaw === 'object'
        ? badgeValueRaw.name ??
          badgeValueRaw.title ??
          badgeValueRaw.label ??
          badgeValueRaw.code ??
          badgeValueRaw.version ??
          badgeValueRaw.id ??
          '—'
        : badgeValueRaw;
  const tokens = getBadgeStyleTokens();

  if (entity?.color) {
    const { className, style } = getBadgePresentationForColor(entity.color, theme);
    return (
      <div className="flex justify-center">
        <span className={className} style={style}>
          {String(badgeValue)}
        </span>
      </div>
    );
  }

  return (
    <div className="flex justify-center">
      <span
        className={badgeClass(
          isRefinedTableUi() ? tokens.neutral : tokens.pill(theme, 'info'),
          theme
        )}
      >
        {String(badgeValue)}
      </span>
    </div>
  );
};

export const renderStatusBadge = (value, column, row, theme) => {
  if (!value) return <div className="flex justify-center"><span className={theme.text.muted}>—</span></div>;
  const entity = resolveBadgeEntity(column, row, value);
  const statusValue = entity?.name || entity?.title || value?.name || value;
  const tokens = getBadgeStyleTokens();

  if (entity?.color) {
    const { className, style } = getBadgePresentationForColor(entity.color, theme);
    return (
      <div className="flex justify-center">
        <span className={className} style={style}>
          {String(statusValue).charAt(0).toUpperCase() + String(statusValue).slice(1)}
        </span>
      </div>
    );
  }

  const key = String(statusValue).toLowerCase().replace(/_/g, ' ');
  return (
    <div className="flex justify-center">
      <span className={badgeClass(tokens.status[key] || tokens.status.cancelled || tokens.neutral)}>
        {String(statusValue).charAt(0).toUpperCase() + String(statusValue).slice(1)}
      </span>
    </div>
  );
};

export const renderBooleanBadge = (value, column, row, theme) => {
  const tokens = getBadgeStyleTokens();
  if (isRefinedTableUi()) {
    return (
      <div className="flex justify-center">
        {value === true ? (
          <span className={badgeClass(tokens.emerald)}>Yes</span>
        ) : value === false ? (
          <span className={badgeClass(tokens.neutral)}>No</span>
        ) : (
          <span className={theme.text.muted}>—</span>
        )}
      </div>
    );
  }
  return (
    <div className="flex justify-center">
      {value === true ? (
        <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
          <CheckCircleFilledIcon className="w-4 h-4" />
          <span className="text-sm font-medium">Yes</span>
        </span>
      ) : value === false ? (
        <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400">
          <XCircleFilledIcon className="w-4 h-4" />
          <span className="text-sm font-medium">No</span>
        </span>
      ) : (
        <span className={theme.text.muted}>—</span>
      )}
    </div>
  );
};

/**
 * Option 2: Truncate with Tooltip
 * Truncates text to single line with ellipsis and shows full text on hover
 */
export const renderTruncateWithTooltip = (value, column, row, theme) => {
  if (!value && value !== 0) return <span className={theme.text.muted}>—</span>;
  
  const text = String(value);
  const maxLength = column.maxLength || 50; // Default to 50 characters
  const shouldTruncate = text.length > maxLength;
  const displayText = shouldTruncate ? text.substring(0, maxLength) + '...' : text;
  
  return (
    <div 
      className="truncate text-sm"
      title={shouldTruncate ? text : undefined}
      style={{ maxWidth: column.width || '200px' }}
    >
      {displayText}
    </div>
  );
};

/**
 * Option 4: Line Clamp
 * Shows text with multiple lines (default 3) then truncates with ellipsis
 * Shows full text in tooltip on hover
 */
export const renderLineClamp = (value, column, row, theme) => {
  if (!value && value !== 0) return <span className={theme.text.muted}>—</span>;
  
  const text = String(value);
  const maxLines = column.maxLines || 3; // Default to 3 lines
  const cellWidth = column.width || '300px';
  
  // Use CSS-based line clamping that works without Tailwind line-clamp utilities
  const lineHeight = 1.5; // Approximate line height in rem (1.5rem = 24px for text-sm)
  const maxHeight = `${maxLines * lineHeight}rem`;
  
  return (
    <div 
      className="text-sm break-words"
      title={text}
      style={{ 
        maxWidth: cellWidth,
        maxHeight: maxHeight,
        overflow: 'hidden',
        display: '-webkit-box',
        WebkitLineClamp: maxLines,
        WebkitBoxOrient: 'vertical',
        lineHeight: `${lineHeight}rem`,
        wordBreak: 'break-word'
      }}
    >
      {text}
    </div>
  );
};

export const renderDate = (value, column, row, theme) => {
  if (!value) return <span className={theme.text.muted}>—</span>;
  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) return <span className={theme.text.muted}>—</span>;
    
    // Use dateFormat from column config if available
    const pattern = column.dateFormat || (getDateFormat && getDateFormat('table')) || 'dd/MM/yyyy';
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    
    const formatted = pattern
      .replace('yyyy', year)
      .replace('MM', month)
      .replace('dd', day);
    
    return (
      <span className={`text-sm ${theme.text.secondary}`}>
        {formatted}
      </span>
    );
  } catch (error) {
    return <span className={theme.text.muted}>—</span>;
  }
};

export const renderOptionalDate = (value, column, row, theme) => {
  if (!value) return '';
  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) return '';

    const pattern = column.dateFormat || (getDateFormat && getDateFormat('table')) || 'dd/MM/yyyy';

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    const formatted = pattern
      .replace('yyyy', year)
      .replace('MM', month)
      .replace('dd', day);

    return (
      <span className={`text-sm ${theme.text.secondary}`}>
        {formatted}
      </span>
    );
  } catch (error) {
    return '';
  }
};

export const renderDateTime = (value, column, row, theme) => {
  if (!value) return <span className={theme.text.muted}>—</span>;
  
  // Check if value is an ISO date string
  const dateStr = value instanceof Date ? value.toISOString() : String(value);
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(dateStr)) {
    // If it doesn't look like an ISO date, try to parse it anyway
    try {
      const date = new Date(value);
      if (isNaN(date.getTime())) return <span className={theme.text.muted}>—</span>;
    } catch {
      return <span className={theme.text.muted}>—</span>;
    }
  }
  
  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) return <span className={theme.text.muted}>—</span>;
    
    // Use dateFormat from column config if available
    const pattern = column.dateFormat || (getDateFormat && getDateFormat('datetime')) || 'dd/MM/yyyy HH:mm';
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    // Replace pattern placeholders (order matters: replace longer patterns first to avoid conflicts)
    const formatted = pattern
      .replace('yyyy', year)
      .replace('MM', month)
      .replace('dd', day)
      .replace('HH', hours)
      .replace('mm', minutes);
    
    return (
      <span className={`text-sm ${theme.text.secondary}`}>
        {formatted}
      </span>
    );
  } catch (error) {
    return <span className={theme.text.muted}>—</span>;
  }
};

export const renderFileCount = (value, column, row, theme, options = {}) => {
  const { onFileCountClick } = options;
  if (value === undefined || value === null) return <span className={theme.text.muted}>—</span>;
  return (
    <div className="flex justify-center items-center gap-1">
      <FileIcon className="w-4 h-4 text-blue-500" />
      <span 
        className={`text-sm font-medium ${theme.text.primary} cursor-pointer hover:text-blue-600`} 
        title={`${value} file(s) - Click to view/download`}
        onClick={() => {
          console.log('📁 File count clicked in AdvancedTable:', { 
            value, 
            row, 
            hasHandler: !!onFileCountClick,
            rowKeys: Object.keys(row),
            fileAttachmentCount: row.fileAttachmentCount,
            columnKey: column.key,
            columnDisplayField: column.displayField
          });
          if (onFileCountClick) {
            onFileCountClick(row, value);
          } else {
            console.log('❌ No onFileCountClick handler provided to AdvancedTable');
          }
        }}
      >
        {value}
      </span>
    </div>
  );
};

export const renderFileList = (value, column, row, theme) => {
  if (value === undefined || value === null || value === 0) return <span className={theme.text.muted}>—</span>;
  
  // If we have the actual file data, display it
  if (row.fileAttachments && Array.isArray(row.fileAttachments)) {
    return (
      <div className="space-y-1 max-h-32 overflow-y-auto">
        {row.fileAttachments.map((file, index) => (
          <div key={index} className="flex items-center gap-2 p-1 bg-gray-50 dark:bg-gray-800 rounded text-xs">
            <FileIcon className="w-3 h-3 text-blue-500 flex-shrink-0" />
            <span className="truncate flex-1">{normalizeFileName(file.fileName || '')}</span>
            <a 
              href={API_ENDPOINTS.fileAttachment.download(file.id)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 underline text-xs flex-shrink-0"
              onClick={(e) => e.stopPropagation()}
            >
              Download
            </a>
          </div>
        ))}
      </div>
    );
  }
  
  // Fallback to count if no file data
  return (
    <div className="flex justify-center items-center gap-1">
      <FileIcon className="w-4 h-4 text-blue-500" />
      <span className="text-sm font-medium">{value} file(s)</span>
    </div>
  );
};

export const renderMyApprovalsFileCount = (value, column, row, theme, options = {}) => {
  const { onFileCountClick } = options;
  
  // Get file attachments from row
  const fileAttachments = row.fileAttachments || [];
  const fileCount = fileAttachments.length;
  
  if (fileCount === 0) {
    return <span className={theme.text.muted}>—</span>;
  }
  
  // If only one file, show count with "View" button that downloads directly
  if (fileCount === 1) {
    const file = fileAttachments[0];
    return (
      <div className="flex items-center gap-2">
        <FileIcon className="w-4 h-4 text-blue-500" />
        <span className={`text-sm font-medium ${theme.text.primary}`}>
          {fileCount} file
        </span>
        <a
          href={API_ENDPOINTS.fileAttachment.download(file.id)}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            "text-xs px-2 py-1 rounded-md transition-colors",
            "bg-blue-600 text-white hover:bg-blue-700",
            "cursor-pointer"
          )}
          onClick={(e) => e.stopPropagation()}
        >
          View
        </a>
      </div>
    );
  }
  
  // If multiple files, show count with "View All" button that opens modal
  return (
    <div className="flex items-center gap-2">
      <FileIcon className="w-4 h-4 text-blue-500" />
      <span 
        className={`text-sm font-medium ${theme.text.primary} cursor-pointer hover:text-blue-600`}
        title={`${fileCount} file(s) - Click to view/download`}
        onClick={(e) => {
          e.stopPropagation();
          if (onFileCountClick) {
            // Pass the files directly if available, otherwise let handler fetch them
            if (fileAttachments.length > 0) {
              // Create a custom handler that uses the files from the row
              const customRow = { ...row, fileAttachments };
              onFileCountClick(customRow, fileCount);
            } else {
              onFileCountClick(row, fileCount);
            }
          }
        }}
      >
        {fileCount} files
      </span>
      <button
        onClick={(e) => {
          e.stopPropagation();
          if (onFileCountClick) {
            if (fileAttachments.length > 0) {
              const customRow = { ...row, fileAttachments };
              onFileCountClick(customRow, fileCount);
            } else {
              onFileCountClick(row, fileCount);
            }
          }
        }}
        className={cn(
          "text-xs px-2 py-1 rounded-md transition-colors",
          "bg-blue-600 text-white hover:bg-blue-700",
          "cursor-pointer"
        )}
      >
        View All
      </button>
    </div>
  );
};

export const renderEntityTypeBadge = (value, column, row, theme) => {
  if (!row.entityType) return <span className={theme.text.muted}>—</span>;
  const entityType = row.entityType;
  const entityTypeValue = entityType?.name || entityType;
  const tokens = getBadgeStyleTokens();
  const color =
    tokens.entityType?.[entityTypeValue] ||
    tokens.entityType?.default ||
    tokens.neutral;
  return (
    <div className="flex items-center justify-center px-2 py-2">
      <span className={badgeClass(color)}>
        {(entityTypeValue)?.replace(/_/g, ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
      </span>
    </div>
  );
};

export const renderEntityDetails = (value, column, row, theme) => {
  if (!row.entityData) return <span className={theme.text.muted}>—</span>;
  const entityData = row.entityData;
  return (
    <div className="flex flex-col space-y-1 px-2 py-2">
      <div className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
        {(entityData.documentCode || entityData.ncrCode || entityData.changeRequestCode || entityData.complaintCode || entityData.riskAssessmentCode || entityData.deviationCode) && (
          <div className="font-semibold text-gray-800 dark:text-gray-200 mb-1">
            {entityData.documentCode || entityData.ncrCode || entityData.changeRequestCode || entityData.complaintCode || entityData.riskAssessmentCode || entityData.deviationCode}
          </div>
        )}
        {(entityData.documentTitle || entityData.ncrTitle || entityData.changeRequestTitle || entityData.complaintTitle || entityData.riskAssessmentTitle || entityData.deviationTitle) && (
          <div className="truncate max-w-[160px] text-xs" title={entityData.documentTitle || entityData.ncrTitle || entityData.changeRequestTitle || entityData.complaintTitle || entityData.riskAssessmentTitle || entityData.deviationTitle}>
            {entityData.documentTitle || entityData.ncrTitle || entityData.changeRequestTitle || entityData.complaintTitle || entityData.riskAssessmentTitle || entityData.deviationTitle}
          </div>
        )}
        {entityData.title && !entityData.documentTitle && !entityData.ncrTitle && !entityData.changeRequestTitle && !entityData.complaintTitle && !entityData.riskAssessmentTitle && !entityData.deviationTitle && (
          <div className="truncate max-w-[160px] text-xs" title={entityData.title}>
            {entityData.title}
          </div>
        )}
        {!entityData.documentCode && !entityData.ncrCode && !entityData.changeRequestCode && !entityData.complaintCode && !entityData.riskAssessmentCode && !entityData.deviationCode && !entityData.documentTitle && !entityData.ncrTitle && !entityData.changeRequestTitle && !entityData.complaintTitle && !entityData.riskAssessmentTitle && !entityData.deviationTitle && !entityData.title && (
          <div className="text-xs text-gray-500 dark:text-gray-400">
            ID: {row.entityId}
          </div>
        )}
      </div>
    </div>
  );
};

export const renderApprovalTypeBadge = (value, column, row, theme) => {
  if (!value) return <span className={theme.text.muted}>—</span>;
  const approvalTypeValue = value?.name || value;
  const tokens = getBadgeStyleTokens();
  const color =
    tokens.approval?.[approvalTypeValue] || tokens.approval?.default || tokens.neutral;
  return (
    <span className={badgeClass(color)}>
      {approvalTypeValue?.charAt(0).toUpperCase() + approvalTypeValue?.slice(1)}
    </span>
  );
};

export const renderWorkflowTypeBadge = (value, column, row, theme) => {
  if (!value) return <div className="flex justify-center"><span className={theme.text.muted}>—</span></div>;
  const workflowTypeValue = value?.name || value;
  const tokens = getBadgeStyleTokens();
  const workflowColors = tokens.workflow;
  // Handle display text
  const getWorkflowLabel = (val) => {
    if (val === 'review') return 'Review Only';
    if (val === 'approval') return 'Approval Only';
    if (val === 'review_approval') return 'Review & Approval';
    return val?.charAt(0).toUpperCase() + val?.slice(1);
  };
  return (
    <div className="flex justify-center">
      <span
        className={badgeClass(
          workflowColors[workflowTypeValue?.toLowerCase()] || workflowColors.default
        )}
      >
        {getWorkflowLabel(workflowTypeValue?.toLowerCase())}
      </span>
    </div>
  );
};

export const renderApprovalProgress = (value, column, row, theme) => {
  // Calculate progress from steps (same logic as ApprovalProgressOverview)
  if (!row.steps || row.steps.length === 0) {
    return <span className={cn("text-sm", theme.text.muted)}>No steps</span>;
  }
  
  // Filter to only required steps
  const requiredSteps = row.steps.filter(step => step.isRequired !== false);
  const allRequiredSteps = requiredSteps.length > 0 ? requiredSteps : row.steps;
  
  // Check for rejected steps
  const hasRejectedStep = allRequiredSteps.some(step => step.rejectedAt || step.rejectedBy);
  if (hasRejectedStep) {
    return (
      <div className="text-sm">
        <span className={cn("text-red-600 font-medium")}>❌ Rejected</span>
      </div>
    );
  }
  
  // Count completed steps based on approvalType
  const isStepCompleted = (step) => {
    if (step.stepType === 'review') {
      return !!(step.reviewedAt || step.reviewedBy) && !step.rejectedAt;
    }
    return !!(step.approvedAt || step.approvedBy) && !step.rejectedAt;
  };
  
  let completedCount = 0;
  let totalRequired = allRequiredSteps.length;
  
  switch (row.approvalType) {
    case 'single':
      completedCount = allRequiredSteps.filter(isStepCompleted).length;
      break;
    case 'sequential':
      let seqCompleted = 0;
      for (let i = 0; i < allRequiredSteps.length; i++) {
        if (isStepCompleted(allRequiredSteps[i])) {
          seqCompleted++;
        } else {
          break;
        }
      }
      completedCount = seqCompleted;
      break;
    case 'parallel':
      completedCount = allRequiredSteps.filter(isStepCompleted).length;
      break;
    case 'any_one':
      const firstCompleted = allRequiredSteps.find(isStepCompleted);
      completedCount = firstCompleted ? 1 : 0;
      totalRequired = 1;
      break;
    case 'review_approval':
      const reviewSteps = allRequiredSteps.filter(s => s.stepType === 'review');
      const approvalSteps = allRequiredSteps.filter(s => s.stepType === 'approval');
      const reviewedCount = reviewSteps.filter(s => s.reviewedAt).length;
      const approvedCount = approvalSteps.filter(s => s.approvedAt).length;
      completedCount = reviewedCount + approvedCount;
      totalRequired = reviewSteps.length + approvalSteps.length;
      break;
    default:
      completedCount = allRequiredSteps.filter(isStepCompleted).length;
  }
  
  const percentage = totalRequired > 0 ? Math.round((completedCount / totalRequired) * 100) : 0;
  const isComplete = completedCount >= totalRequired;
  
  return (
    <div className="text-sm">
      <div className="flex items-center justify-between mb-1">
        <span className={cn(
          isComplete ? "text-green-600 font-medium" : 
          hasRejectedStep ? "text-red-600 font-medium" : 
          "text-gray-600"
        )}>
          {isComplete ? '✅' : ''} {completedCount}/{totalRequired}
        </span>
        {!isComplete && !hasRejectedStep && (
          <span className={cn("text-xs", theme.text.muted)}>({percentage}%)</span>
        )}
      </div>
      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
        <div 
          className={cn(
            "h-2 rounded-full transition-all",
            hasRejectedStep ? 'bg-red-600' :
            isComplete ? 'bg-green-600' : 'bg-blue-600'
          )}
          style={{ width: `${Math.max(0, Math.min(100, percentage))}%` }}
        ></div>
      </div>
    </div>
  );
};

export const renderToggleSwitch = (value, column, row, theme) => {
  const toggleConfig = column?.toggleConfig || {};
  const enabledLabel = toggleConfig.enabledLabel || 'On';
  const disabledLabel = toggleConfig.disabledLabel || 'Off';
  const isEnabled = Boolean(value);

  return (
    <div className="flex items-center justify-center">
      <span
        className={badgeClass(isEnabled ? getBadgeStyleTokens().emerald : getBadgeStyleTokens().neutral)}
      >
        {isEnabled ? enabledLabel : disabledLabel}
      </span>
    </div>
  );
};

export const renderUserDisplay = (value, column, row, theme) => {
  if (!value) return <span className={theme.text.muted}>—</span>;
  // Handle both object and string values
  if (typeof value === 'object' && value !== null) {
    const displayName = value.userDisplayName || 
                       value.name || 
                       `${value.userFirstName || ''} ${value.userLastName || ''}`.trim() || 
                       value.userEmail || 
                       'Unknown User';
    return (
      <div className="flex items-center space-x-2 px-2 py-2">
        <div className={cn('w-6 h-6 rounded-full flex items-center justify-center', badgeClass(getBadgeStyleTokens().blue))}>
          <span className="text-xs font-semibold">
            {displayName.charAt(0).toUpperCase()}
          </span>
        </div>
        <span className={cn("text-sm font-medium", theme.text.primary)}>
          {displayName}
        </span>
      </div>
    );
  }
  return (
    <div className="flex items-center space-x-2 px-2 py-2">
      <div className={cn('w-6 h-6 rounded-full flex items-center justify-center', badgeClass(getBadgeStyleTokens().blue))}>
        <span className="text-xs font-semibold">
          {value.charAt(0).toUpperCase()}
        </span>
      </div>
      <span className={cn("text-sm font-medium", theme.text.primary)}>
        {value}
      </span>
    </div>
  );
};

export const renderOptionalUserDisplay = (value, column, row, theme) => {
  if (!value) return '';
  if (typeof value === 'object' && value !== null) {
    const displayName = value.userDisplayName ||
      value.name ||
      `${value.userFirstName || ''} ${value.userLastName || ''}`.trim() ||
      value.userEmail ||
      '';
    if (!displayName) return '';
    return (
      <div className="flex items-center space-x-2 px-2 py-2">
        <div className={cn('w-6 h-6 rounded-full flex items-center justify-center', badgeClass(getBadgeStyleTokens().blue))}>
          <span className="text-xs font-semibold">
            {displayName.charAt(0).toUpperCase()}
          </span>
        </div>
        <span className={cn("text-sm font-medium", theme.text.primary)}>
          {displayName}
        </span>
      </div>
    );
  }
  const displayName = String(value || '').trim();
  if (!displayName) return '';
  return (
    <div className="flex items-center space-x-2 px-2 py-2">
      <div className={cn('w-6 h-6 rounded-full flex items-center justify-center', badgeClass(getBadgeStyleTokens().blue))}>
        <span className="text-xs font-semibold">
          {displayName.charAt(0).toUpperCase()}
        </span>
      </div>
      <span className={cn("text-sm font-medium", theme.text.primary)}>
        {displayName}
      </span>
    </div>
  );
};

export const renderPriorityBadge = (value, column, row, theme) => {
  if (!value) return <div className="flex justify-center"><span className={theme.text.muted}>—</span></div>;
  const priorityValue = value?.name || value;
  const tokens = getBadgeStyleTokens();
  return (
    <div className="flex justify-center">
      <span
        className={badgeClass(
          tokens.priority[priorityValue?.toLowerCase()] || tokens.priority.default
        )}
      >
        {priorityValue?.charAt(0).toUpperCase() + priorityValue?.slice(1)}
      </span>
    </div>
  );
};

/**
 * Array renderer - displays array values (typically for multi-select fields)
 * Shows array items as badges, or count if many items
 */
export const renderArray = (value, column, row, theme) => {
  // If displayField is specified, use that value from the row instead
  const arrayValue = column.displayField && row[column.displayField] 
    ? row[column.displayField] 
    : (Array.isArray(value) ? value : []);
  
  if (!arrayValue || arrayValue.length === 0) {
    return <span className={cn("text-sm", theme.text.muted)}>—</span>;
  }
  
  // If array contains objects (like departments with name property)
  if (arrayValue.length > 0 && typeof arrayValue[0] === 'object') {
    // Show first 3 items as badges, then count if more
    const displayItems = arrayValue.slice(0, 3);
    const remainingCount = arrayValue.length - 3;
    
    return (
      <div className="flex flex-wrap items-center gap-1">
        {displayItems.map((item, idx) => {
          // Check for common display name fields
          const displayName = item.userDisplayName 
            || item.name 
            || item.title 
            || item.label 
            || (item.userFirstName && item.userLastName ? `${item.userFirstName} ${item.userLastName}` : null)
            || `Item ${idx + 1}`;
          return (
            <span key={idx} className={badgeClass(getBadgeStyleTokens().neutral)}>
              {displayName}
            </span>
          );
        })}
        {remainingCount > 0 && (
          <span className={cn("text-xs font-medium", theme.text.muted)}>
            +{remainingCount} more
          </span>
        )}
      </div>
    );
  }
  
  // If array contains primitive values
  const displayItems = arrayValue.slice(0, 3);
  const remainingCount = arrayValue.length - 3;
  
  return (
    <div className="flex flex-wrap items-center gap-1">
      {displayItems.map((item, idx) => (
        <span key={idx} className={badgeClass(getBadgeStyleTokens().neutral)}>
          {String(item)}
        </span>
      ))}
      {remainingCount > 0 && (
        <span className={cn("text-xs font-medium", theme.text.muted)}>
          +{remainingCount} more
        </span>
      )}
    </div>
  );
};

export const renderTodoTasks = (value, column, row, theme) => {
  let tasks = [];

  if (Array.isArray(value)) {
    tasks = value;
  } else if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      tasks = Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      tasks = [];
    }
  }

  const normalized = tasks
    .map((task) => ({
      task: String(task?.task ?? task?.Task ?? '').trim(),
      isDone: Boolean(task?.isDone ?? task?.IsDone),
    }))
    .filter((task) => task.task);

  if (normalized.length === 0) {
    return <span className={cn("text-sm", theme.text.muted)}>—</span>;
  }

  return (
    <div className="space-y-1">
      {normalized.slice(0, 3).map((task, idx) => (
        <div
          key={`${task.task}-${idx}`}
          className={cn("text-xs", task.isDone ? 'line-through opacity-70' : theme.text.primary)}
          title={task.task}
        >
          {task.task}
        </div>
      ))}
      {normalized.length > 3 ? (
        <div className={cn("text-[10px]", theme.text.muted)}>+{normalized.length - 3} more</div>
      ) : null}
    </div>
  );
};

export const renderTodoTaskOpenTotal = (value, column, row, theme, options = {}) => {
  const { onTodoTaskCountClick } = options;
  let tasks = [];

  if (Array.isArray(value)) {
    tasks = value;
  } else if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      tasks = Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      tasks = [];
    }
  }

  const normalized = tasks
    .map((task) => ({
      task: String(task?.task ?? task?.Task ?? '').trim(),
      isDone: Boolean(task?.isDone ?? task?.IsDone),
    }))
    .filter((task) => task.task);

  const total = normalized.length;
  const open = normalized.filter((task) => !task.isDone).length;

  if (total === 0) {
    return <span className={cn("text-sm", theme.text.muted)}>0/0</span>;
  }

  return (
    <button
      type="button"
      className={cn("text-sm font-medium underline decoration-dotted hover:opacity-80", theme.text.primary)}
      onClick={(event) => {
        event.stopPropagation();
        if (onTodoTaskCountClick) {
          onTodoTaskCountClick(row, normalized);
        }
      }}
      title="View tasks"
    >
      {open}/{total}
    </button>
  );
};

/**
 * Registry of all cell renderers
 * Maps cellRenderer string values to their render functions
 */
export const CELL_RENDERERS = {
  color: renderColor,
  boolean: renderBoolean,
  badge: renderBadge,
  truncateWithTooltip: renderTruncateWithTooltip,
  lineClamp: renderLineClamp,
  statusBadge: renderStatusBadge,
  booleanBadge: renderBooleanBadge,
  date: renderDate,
  optionalDate: renderOptionalDate,
  dateTime: renderDateTime,
  datetime: renderDateTime, // Alias for dateTime
  fileCount: renderFileCount,
  fileList: renderFileList,
  myApprovalsFileCount: renderMyApprovalsFileCount,
  entityTypeBadge: renderEntityTypeBadge,
  entityDetails: renderEntityDetails,
  approvalTypeBadge: renderApprovalTypeBadge,
  workflowTypeBadge: renderWorkflowTypeBadge,
  approvalProgress: renderApprovalProgress,
  toggleSwitch: renderToggleSwitch,
  userDisplay: renderUserDisplay,
  optionalUserDisplay: renderOptionalUserDisplay,
  priorityBadge: renderPriorityBadge,
  array: renderArray,
  todoTasks: renderTodoTasks,
  todoTaskOpenTotal: renderTodoTaskOpenTotal,
};

/**
 * Default renderer for objects, arrays, and other types
 */
export const renderDefault = (value, column, row, theme, options = {}) => {
  const { spacing, actionsVisibility } = options;
  
  // Fallback: Check if value is an ISO date string and format it (catches cases where cellRenderer wasn't set correctly)
  if (value && typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
    try {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        // Use dateFormat from column config if available, otherwise use default format
        const pattern = column.dateFormat || (getDateFormat && getDateFormat('datetime')) || 'dd/MM/yyyy HH:mm';
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const formatted = pattern
          .replace('yyyy', year)
          .replace('MM', month)
          .replace('dd', day)
          .replace('HH', hours)
          .replace('mm', minutes);
        return (
          <span className={`text-sm ${theme.text.secondary}`}>{formatted}</span>
        );
      }
    } catch (error) {
      // Fall through to default handling
    }
  }
  
  if (value && typeof value === 'object') {
    if (React.isValidElement(value)) {
      if (column.key === 'actions') {
        return React.cloneElement(value, {
          className: cn(
            value.props.className,
            spacing,
            actionsVisibility === 'hover' ? 'opacity-0 group-hover:opacity-100 transition-opacity duration-200' : 'opacity-100'
          )
        });
      }
      return value;
    }
    
    if (Array.isArray(value)) {
      if (value.length > 0 && value[0] && typeof value[0] === 'object' && 
          (value[0].stepNumber !== undefined || value[0].stepName !== undefined)) {
        return <span className="text-sm font-medium">{value.length} step{value.length !== 1 ? 's' : ''}</span>;
      }
      return value.length > 0 ? `[${value.length} items]` : '[]';
    }
    
    const displayValue = value.name || 
                        value.title || 
                        value.label || 
                        value.typeName || 
                        value.statusName ||
                        (value.id ? `ID: ${value.id}` : '[Object]');
    
    return String(displayValue);
  }
  
  return value ?? '—';
};

